import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { boundLoginAttempt } from './login-attempt';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment'; // [auth-mt]
import { TenantProfileService } from '../tenant-profile/tenant-profile.service';
import { ParkingProfileService } from '../tenant-profile/parking-profile.service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  // [auth-mt] OpenIddict token storage (in-memory)
  private accessToken: string | null = null; // [auth-mt]
  private refreshTokenValue: string | null = null; // [auth-mt]
  private expiresAtEpochSec = 0; // [auth-mt]
  private tenantIdValue: string | number | null = null; // [auth-mt] prefer dynamic tenant from login
  private tenantTimeZone: string = 'UTC';
  private tenantLanguage: string = 'es';

  private tokenEndpoint = `${environment.apiBaseUrl}/connect/token`; // [auth-mt]
  private revokeEndpoint = `${environment.apiBaseUrl}/connect/revoke`; // [auth-mt]
  private currentUserSubject = new BehaviorSubject<any | null>(null);
  public readonly currentUser$ = this.currentUserSubject.asObservable();

  /** Nombre visible del estacionamiento (tenant) para el header; null si no aplica (p. ej. superadmin). */
  private tenantDisplayNameSubject = new BehaviorSubject<string | null>(null);
  readonly tenantDisplayName$ = this.tenantDisplayNameSubject.asObservable();

  constructor(
    private http: HttpClient,
    private tenantProfileService: TenantProfileService,
    private parkingProfileService: ParkingProfileService,
    private injector: Injector
  ) {
    // [auth-mt] Load refresh token from localStorage (remembered device) or sessionStorage
    // Verificar expiración de tokens recordados
    const rememberedTokenData = this.getRememberedToken();
    if (rememberedTokenData) {
      this.refreshTokenValue = rememberedTokenData;
    } else {
      this.refreshTokenValue = sessionStorage.getItem('refresh_token');
    }

    // [auth-mt] Load tenant id from localStorage (remembered device) or sessionStorage
    const persistedTenant = localStorage.getItem('tenant_id') || sessionStorage.getItem('tenant_id');
    if (persistedTenant != null) {
      this.tenantIdValue = persistedTenant;
      try { if (!environment.production) { console.log('[auth-mt][debug] Loaded tenant from storage:', this.tenantIdValue); } } catch { }
    } else {
      try { if (!environment.production) { console.log('[auth-mt][debug] No tenant in storage at startup'); } } catch { }
    }

    // Load tenant timezone from storage
    const persistedTz = localStorage.getItem('tenant_timezone') || sessionStorage.getItem('tenant_timezone');
    if (persistedTz) {
      this.tenantTimeZone = persistedTz;
    }

    // Idioma del tenant (no selector por usuario)
    const persistedLang =
      localStorage.getItem('tenant_language') ||
      sessionStorage.getItem('tenant_language') ||
      'es';
    this.applyTenantLanguage(persistedLang);

    const storedUser = localStorage.getItem('currentUser');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        this.currentUserSubject.next(user);
        this.applyTenantLanguage(user?.language ?? user?.Language ?? persistedLang);
      } catch {
        localStorage.removeItem('currentUser');
        this.currentUserSubject.next(null);
      }
    }

    // [debug] Exponer el servicio globalmente para depuración en consola
    if (typeof window !== 'undefined' && !environment.production) {
      (window as any).__authService = this;
      console.log('💡 Servicio de autenticación disponible en: window.__authService');
      console.log('💡 Ejecuta: window.__authService.checkPermissionsStatus().subscribe()');
    }
  }

  // [security] Obtener token recordado verificando expiración
  private getRememberedToken(): string | null {
    try {
      const tokenDataStr = localStorage.getItem('refresh_token');
      if (!tokenDataStr) {
        return null;
      }

      // Intentar parsear como objeto (nuevo formato con expiración)
      try {
        const tokenData = JSON.parse(tokenDataStr);
        if (tokenData && tokenData.token && tokenData.expiresAt) {
          // Verificar si el token ha expirado
          if (Date.now() > tokenData.expiresAt) {
            // Token expirado, limpiar
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('refresh_token_expires_at');
            return null;
          }
          return tokenData.token;
        }
      } catch {
        // Formato antiguo (solo string), mantener compatibilidad
        return tokenDataStr;
      }
    } catch (error) {
      console.error('Error al obtener token recordado:', error);
      return null;
    }
    return null;
  }

  // [auth-mt] Password grant login against OpenIddict
  login(credentials: { email: string; password: string }, rememberDevice: boolean = false): Observable<any> {
    const body = new HttpParams()
      .set('grant_type', 'password')
      .set('username', credentials.email) // [auth-mt] map email->username
      .set('password', credentials.password)
      .set('scope', 'api offline_access')
      .set('client_id', environment.openiddictClientId);

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      // 'X-Tenant-Id' intentionally omitted for login [auth-mt]
    });

    // HttpClient no vence solo. Un /connect/token o /api/auth/me pendiente dejaba
    // "Iniciando sesión…" hasta el corte del proxy (~60 s) o para siempre.
    return boundLoginAttempt(this.http.post<any>(this.tokenEndpoint, body.toString(), { headers }).pipe(
      tap((res: any) => {
        this.storeTokens(res, rememberDevice);
        // Notificar que el token cambió para que el servicio de permisos recargue
        console.log('[AuthService] Tokens almacenados, notificando cambio de usuario');
      }), // [auth-mt]
      // [auth-mt] Si no hay tenant en environment, obtenerlo de /api/auth/me y cargar perfil
      switchMap((res: any) =>
        this.ensureTenantFromAuthMeIfMissing(rememberDevice).pipe(
          switchMap(() => this.fetchAndStoreCurrentUser()),
          tap(() => {
            // Forzar recarga de permisos después de obtener el usuario
            console.log('[AuthService] Usuario cargado, el servicio de permisos debería recargar');
          }),
          map(() => res)
        )
      ),
    ));
  }

  // [auth-mt] Refresh tokens using refresh_token grant
  refresh(): Observable<boolean> {
    // Obtener token recordado con verificación de expiración
    const rememberedToken = this.getRememberedToken();
    const tokenToUse = this.refreshTokenValue || rememberedToken || sessionStorage.getItem('refresh_token');

    if (!tokenToUse) {
      return of(false);
    }

    const body = new HttpParams()
      .set('grant_type', 'refresh_token')
      .set('refresh_token', tokenToUse)
      .set('scope', 'api offline_access')
      .set('client_id', environment.openiddictClientId);

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      // Optional X-Tenant-Id can be added; omitted per contract [auth-mt]
    });

    // Determinar si se debe recordar el dispositivo basado en dónde está el refresh token
    const rememberDevice = !!rememberedToken;

    return this.http.post<any>(this.tokenEndpoint, body.toString(), { headers }).pipe(
      tap((res: any) => this.storeTokens(res, rememberDevice)),
      switchMap(() =>
        this.ensureTenantFromAuthMeIfMissing(rememberDevice).pipe(
          switchMap(() => this.fetchAndStoreCurrentUser())
        )
      ),
      map(() => true),
      catchError(() => of(false))
    );
  }

  logout(): void {
    // Revocar antes de borrar: el cuerpo ya lleva el refresh token y el usuario no espera la respuesta.
    const refreshToken = this.resolveRefreshToken();
    if (refreshToken) {
      this.revokeRefreshToken(refreshToken);
    }
    this.clearLocalSession();
  }

  /** Mismo token que usaría refresh(). Null si no hay sesión renovable. */
  private resolveRefreshToken(): string | null {
    const rememberedToken = this.getRememberedToken();
    return this.refreshTokenValue || rememberedToken || sessionStorage.getItem('refresh_token');
  }

  /**
   * POST /connect/revoke (RFC 7009). Sin Authorization.
   * El client_id es el mismo público del login: uno distinto responde 200 sin revocar.
   * Si falla o no hay respuesta, se registra el código y se sigue: la sesión local ya se limpia.
   */
  private revokeRefreshToken(refreshToken: string): void {
    const body = new HttpParams()
      .set('token', refreshToken)
      .set('token_type_hint', 'refresh_token')
      .set('client_id', environment.openiddictClientId);

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    this.http
      .post(this.revokeEndpoint, body.toString(), { headers })
      .pipe(
        catchError((err: unknown) => {
          this.logRevokeFailure(err);
          return of(null);
        })
      )
      .subscribe();
  }

  /** Registra el fallo sin el token ni el cuerpo (el servidor puede eco del token). */
  private logRevokeFailure(err: unknown): void {
    let status: number | string = 'sin respuesta';
    let errorCode: string | undefined;
    if (err instanceof HttpErrorResponse) {
      status = err.status > 0 ? err.status : 'sin respuesta';
      const body = err.error;
      if (body && typeof body === 'object' && typeof (body as { error?: unknown }).error === 'string') {
        const code = (body as { error: string }).error;
        if (code === 'invalid_request' || code === 'invalid_client' || code === 'unauthorized_client') {
          errorCode = code;
        }
      }
    }
    console.warn('[AuthService] No se pudo revocar la sesión en el servidor.', {
      status,
      ...(errorCode ? { error: errorCode } : {}),
    });
  }

  // [auth-mt] Clear in-memory and persisted refresh token from both storages
  private clearLocalSession(): void {
    this.accessToken = null;
    this.refreshTokenValue = null;
    this.expiresAtEpochSec = 0;
    sessionStorage.removeItem('refresh_token'); // [auth-mt]
    sessionStorage.removeItem('tenant_id'); // [auth-mt]
    localStorage.removeItem('refresh_token'); // [auth-mt] Clear remembered device token
    localStorage.removeItem('refresh_token_expires_at'); // [security] Clear expiration timestamp
    localStorage.removeItem('tenant_id'); // [auth-mt] Clear remembered tenant
    this.tenantDisplayNameSubject.next(null);
    this.setCurrentUser(null);
    try { if (!environment.production) { console.log('[auth-mt][debug] Logged out; cleared tokens and tenant'); } } catch { }
  }


  // [auth-mt] Si no hay tenant en environment ni en memoria, intenta obtenerlo de /api/auth/me
  private ensureTenantFromAuthMeIfMissing(rememberDevice: boolean = false): Observable<void> {
    const fromEnv = (environment as any).tenantId ?? null;
    if (fromEnv != null || this.tenantIdValue != null) {
      return of(void 0);
    }
    const url = `${environment.apiBaseUrl}/api/auth/me`;
    return this.http.get<any>(url).pipe(
      tap((info) => {
        const t = (info?.tenantId ?? info?.tenant_id ?? info?.tenant ?? null);
        if (t != null) {
          this.tenantIdValue = typeof t === 'number' ? t : String(t);
          // Guardar en localStorage si se recordó el dispositivo, sino en sessionStorage
          try {
            if (rememberDevice) {
              localStorage.setItem('tenant_id', String(this.tenantIdValue));
            } else {
              sessionStorage.setItem('tenant_id', String(this.tenantIdValue));
            }
          } catch { }
          try { if (!environment.production) { console.log('[auth-mt][debug] Captured tenant from /api/auth/me ->', this.tenantIdValue); } } catch { }
        } else {
          try { if (!environment.production) { console.warn('[auth-mt][debug] /api/auth/me no devolvió tenant_id/tenantId'); } } catch { }
        }
      }),
      map(() => void 0),
      catchError((err) => {
        try { if (!environment.production) { console.warn('[auth-mt][debug] Error en /api/auth/me para obtener tenant', err); } } catch { }
        return of(void 0);
      })
    );
  }

  private fetchAndStoreCurrentUser(): Observable<void> {
    const url = `${environment.apiBaseUrl}/api/auth/me`;
    return this.http.get<any>(url).pipe(
      tap((user) => {
        this.setCurrentUser(user);
      }),
      switchMap(() => this.refreshTenantDisplayName()),
      map(() => void 0),
      catchError((err) => {
        this.setCurrentUser(null);
        this.tenantDisplayNameSubject.next(null);
        try {
          if (!environment.production) {
            console.warn('[auth-mt][debug] Error fetching current user profile', err);
          }
        } catch { }
        return of(void 0);
      })
    );
  }

  /**
   * Carga el nombre del tenant para el header. Sin efecto para SuperAdministrator.
   */
  refreshTenantDisplayName(): Observable<void> {
    const user = this.getCurrentUserSnapshot();
    if (!this.isAuthenticated() || this.isSuperAdministrator(user)) {
      this.tenantDisplayNameSubject.next(null);
      return of(void 0);
    }
    return this.tenantProfileService.getMyTenant().pipe(
      tap((t) => {
        const n = (t.displayName ?? '').trim();
        this.tenantDisplayNameSubject.next(n.length > 0 ? n : null);
        if (t.parkingProfile != null) {
          this.parkingProfileService.setProfile(t.parkingProfile);
        }
      }),
      map(() => void 0),
      catchError(() => {
        this.tenantDisplayNameSubject.next(null);
        return of(void 0);
      })
    );
  }

  /** Actualiza el nombre mostrado en el header tras guardar en "Mi organización" (sin recargar la página). */
  setTenantDisplayName(name: string | null): void {
    if (name === null || name === undefined) {
      this.tenantDisplayNameSubject.next(null);
      return;
    }
    const n = String(name).trim();
    this.tenantDisplayNameSubject.next(n.length > 0 ? n : null);
  }

  private setCurrentUser(user: any | null): void {
    if (user) {
      try {
        localStorage.setItem('currentUser', JSON.stringify(user));
      } catch {
        // ignore storage errors
      }
      // Persist tenant timezone
      const tz = user?.timeZone ?? user?.timezone ?? 'UTC';
      this.tenantTimeZone = tz;
      try {
        localStorage.setItem('tenant_timezone', tz);
        sessionStorage.setItem('tenant_timezone', tz);
      } catch { }
      this.applyTenantLanguage(user?.language ?? user?.Language);
    } else {
      localStorage.removeItem('currentUser');
      localStorage.removeItem('tenant_timezone');
      sessionStorage.removeItem('tenant_timezone');
      localStorage.removeItem('tenant_language');
      sessionStorage.removeItem('tenant_language');
      localStorage.removeItem('selectedLanguage');
      this.tenantTimeZone = 'UTC';
      this.applyTenantLanguage('es');
    }
    this.currentUserSubject.next(user);
  }

  getTenantTimeZone(): string {
    return this.tenantTimeZone || 'UTC';
  }

  getTenantLanguage(): string {
    return this.tenantLanguage || 'es';
  }

  /** Aplica el idioma del tenant a ngx-translate (sin override por usuario). */
  private applyTenantLanguage(raw: unknown): void {
    const lang = this.normalizeLanguage(raw);
    this.tenantLanguage = lang;
    try {
      localStorage.setItem('tenant_language', lang);
      sessionStorage.setItem('tenant_language', lang);
      localStorage.removeItem('selectedLanguage');
    } catch { }
    // Diferir para evitar ciclo DI Auth → Translate → HttpClient → interceptor → Auth
    queueMicrotask(() => {
      try {
        const translate = this.injector.get(TranslateService);
        translate.setDefaultLang(lang);
        translate.use(lang);
      } catch { /* Translate aún no disponible */ }
    });
  }

  private normalizeLanguage(raw: unknown): string {
    if (typeof raw !== 'string' || !raw.trim()) {
      return 'es';
    }
    const code = raw.trim().toLowerCase().split('-')[0];
    return code === 'en' ? 'en' : 'es';
  }

  getCurrentUserSnapshot(): any | null {
    return this.currentUserSubject.value;
  }

  getDisplayName(user: any | null): string {
    const defaultName = 'User';
    if (!user) {
      return defaultName;
    }

    const pick = (obj: any, keys: string[]): string | null => {
      for (const key of keys) {
        const altKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
        const value = obj?.[key] ?? obj?.[altKey];
        if (typeof value === 'string' && value.trim().length > 0) {
          return value.trim();
        }
      }
      return null;
    };

    const explicitFullName = pick(user, ['fullName', 'full_name']);
    if (explicitFullName) {
      return explicitFullName;
    }

    const first = pick(user, ['name', 'firstName', 'givenName', 'given_name', 'nombre']);
    const last = pick(user, ['surname', 'lastName', 'familyName', 'family_name', 'apellido']);

    const combined = [first, last].filter(Boolean).join(' ').trim();
    if (combined.length > 0) {
      return combined;
    }
    return (
      user.username ??
      user.userName ??
      user.email ??
      user.mail ??
      defaultName
    );
  }

  getDisplayRole(user: any | null): string {
    const defaultRole = 'Guest';
    if (!user) {
      return defaultRole;
    }
    const fromArray = Array.isArray(user.roles)
      ? user.roles.map((r: any) => (typeof r === 'string' ? r.trim() : '')).find((r: string) => r.length > 0)
      : null;
    return (
      fromArray ??
      user.role ??
      user.roleName ??
      user.roleDescription ??
      user.perfil ??
      defaultRole
    );
  }

  private normalizeRoleName(role: unknown): string {
    return typeof role === 'string' ? role.trim().toLowerCase() : '';
  }

  /**
   * SuperAdministrator de plataforma. La comparación ignora mayúsculas (claims/JWT pueden variar el casing).
   */
  isSuperAdministrator(user: any | null): boolean {
    if (!user) {
      return false;
    }
    const roles = Array.isArray(user.roles) ? user.roles : [];
    if (roles.some((r: unknown) => this.normalizeRoleName(r) === 'superadministrator')) {
      return true;
    }
    const dr = String(this.getDisplayRole(user) ?? '').trim().toLowerCase();
    return dr === 'superadministrator';
  }

  /**
   * Administrador del estacionamiento (rol Identity Administrator), excluyendo SuperAdministrator.
   */
  isTenantAdministrator(user: any | null): boolean {
    if (!user || this.isSuperAdministrator(user)) {
      return false;
    }
    const roles = Array.isArray(user.roles) ? user.roles : [];
    return roles.some((r: unknown) => this.normalizeRoleName(r) === 'administrator');
  }

  /**
   * Rol Identity Supervisor (alineado con API de incidentes: listado global).
   */
  hasParkingSupervisorRole(user: any | null): boolean {
    if (!user || this.isSuperAdministrator(user)) {
      return false;
    }
    const roles = Array.isArray(user.roles) ? user.roles : [];
    return roles.some((r: unknown) => this.normalizeRoleName(r) === 'supervisor');
  }

  // [auth-mt] Status helpers
  isAuthenticated(): boolean {
    return !!this.accessToken && Date.now() / 1000 < this.expiresAtEpochSec;
  }

  // [auth-mt] Backward-compatible method for services expecting token in memory
  getToken(): string | null {
    return this.getAccessToken();
  }

  // [auth-mt] Explicit getter
  getAccessToken(): string | null {
    return this.accessToken;
  }

  // [auth-mt] Tenant: si environment tiene valor, se usa; si no, el capturado vía /api/auth/me
  getTenantId(): string | number {
    const fromEnv = (environment as any).tenantId ?? null;
    const fromMe = this.tenantIdValue;
    const value: any = (fromEnv ?? fromMe ?? null);
    try {
      if (!environment.production) {
        console.log('[auth-mt][debug] getTenantId ->', value, '(source:', fromEnv != null ? 'env' : fromMe != null ? 'me' : 'none', ')');
      }
    } catch { }
    return value as any;
  }

  // [auth-mt] Helper for guard
  hasRefreshToken(): boolean {
    // Verificar token recordado con expiración
    const rememberedToken = this.getRememberedToken();
    return !!this.refreshTokenValue || !!sessionStorage.getItem('refresh_token') || !!rememberedToken;
  }

  // [auth-mt] Internal: persist tokens in memory and refresh in sessionStorage or localStorage
  private storeTokens(res: any, rememberDevice: boolean = false): void {
    this.accessToken = res?.access_token || null;
    this.refreshTokenValue = res?.refresh_token || null;
    const expiresIn = Number(res?.expires_in || 0);
    this.expiresAtEpochSec = Math.floor(Date.now() / 1000) + (isNaN(expiresIn) ? 0 : expiresIn);

    if (this.refreshTokenValue) {
      // Limpiar tokens anteriores del otro storage
      if (rememberDevice) {
        sessionStorage.removeItem('refresh_token');
        // Guardar token con timestamp de expiración (30 días para dispositivos recordados)
        const expirationTime = Date.now() + (30 * 24 * 60 * 60 * 1000); // 30 días
        const tokenData = {
          token: this.refreshTokenValue,
          expiresAt: expirationTime
        };
        localStorage.setItem('refresh_token', JSON.stringify(tokenData));
        localStorage.setItem('refresh_token_expires_at', expirationTime.toString());
      } else {
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('refresh_token_expires_at');
        sessionStorage.setItem('refresh_token', this.refreshTokenValue);
      }
    }
    // [auth-mt] No derivamos tenant del token (opaco). Se obtiene aparte vía /api/auth/me
    try { if (!environment.production) { console.log('[auth-mt][debug] Tokens almacenados (tenant no derivado del token)', rememberDevice ? 'en localStorage' : 'en sessionStorage'); } } catch { }
  }

  /**
   * Obtiene el ID del usuario actualmente autenticado.
   * Prioriza snapshot en memoria, luego localStorage y claim `sub` del JWT.
   */
  getUserId(): string | null {
    const snapshot = this.getCurrentUserSnapshot();
    const fromSnapshot = this.extractUserId(snapshot);
    if (fromSnapshot) {
      return fromSnapshot;
    }

    try {
      const stored = localStorage.getItem('currentUser');
      if (stored) {
        const fromStorage = this.extractUserId(JSON.parse(stored));
        if (fromStorage) {
          return fromStorage;
        }
      }
    } catch {
      // ignore parse/storage errors
    }

    return this.getUserIdFromAccessToken();
  }

  private extractUserId(user: any | null | undefined): string | null {
    if (!user) {
      return null;
    }
    const raw = user.id ?? user.userId ?? user.Id ?? user.UserId;
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }
    return String(raw).trim();
  }

  private getUserIdFromAccessToken(): string | null {
    const token = this.getAccessToken();
    if (!token) {
      return null;
    }

    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }

      let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      while (base64.length % 4) {
        base64 += '=';
      }

      const decoded = JSON.parse(atob(base64));
      const sub = decoded?.sub ?? decoded?.nameid ?? decoded?.user_id;
      return sub ? String(sub).trim() : null;
    } catch {
      return null;
    }
  }

  /**
   * Solicita reset de contraseña
   * @param email Email del usuario
   * @returns Observable con la respuesta
   */
  forgotPassword(email: string): Observable<any> {
    const url = `${environment.apiBaseUrl}/api/auth/forgot-password`;
    return this.http.post<any>(url, { email });
  }

  /**
   * Resetea la contraseña con el token recibido por email
   * @param token Token de reset
   * @param tenantId ID del tenant
   * @param newPassword Nueva contraseña
   * @param confirmPassword Confirmación de contraseña
   * @returns Observable con la respuesta
   */
  resetPassword(token: string, tenantId: number, newPassword: string, confirmPassword: string): Observable<any> {
    const url = `${environment.apiBaseUrl}/api/auth/reset-password`;
    return this.http.post<any>(url, {
      token,
      tenantId,
      newPassword,
      confirmPassword
    });
  }

  /**
   * Verifica el email del usuario con el token recibido
   * @param token Token de verificación
   * @param email Email del usuario
   * @param tenantId ID del tenant
   * @returns Observable con la respuesta
   */
  verifyEmail(token: string, email: string, tenantId: number): Observable<any> {
    const url = `${environment.apiBaseUrl}/api/auth/verify-email`;
    return this.http.post<any>(url, {
      token,
      email,
      tenantId
    });
  }

  /**
   * Reenvía el email de verificación
   * @param email Email del usuario
   * @returns Observable con la respuesta
   */
  resendVerificationEmail(email: string): Observable<any> {
    const url = `${environment.apiBaseUrl}/api/auth/resend-verification-email`;
    return this.http.post<any>(url, { email });
  }

  /**
   * Establece la contraseña inicial después de verificar el email
   * @param token Token de verificación
   * @param email Email del usuario
   * @param tenantId ID del tenant
   * @param password Nueva contraseña
   * @param confirmPassword Confirmación de contraseña
   * @returns Observable con la respuesta
   */
  setInitialPassword(token: string, email: string, tenantId: number, password: string, confirmPassword: string): Observable<any> {
    const url = `${environment.apiBaseUrl}/api/auth/set-initial-password`;
    return this.http.post<any>(url, {
      token,
      email,
      tenantId,
      password,
      confirmPassword
    });
  }

  /**
   * Fuerza un refresh del token para obtener los permisos actualizados
   * Útil cuando se han agregado permisos al rol después de iniciar sesión
   */
  forceTokenRefresh(): Observable<boolean> {
    console.log('[AuthService] Forzando refresh del token para actualizar permisos...');
    return this.refresh().pipe(
      tap((success) => {
        if (success) {
          console.log('[AuthService] ✅ Token refrescado exitosamente');
          // Recargar el usuario para obtener permisos actualizados
          this.fetchAndStoreCurrentUser().subscribe();
        } else {
          console.warn('[AuthService] ⚠️ No se pudo refrescar el token. Cierra sesión e inicia sesión de nuevo.');
        }
      })
    );
  }

  /**
   * Verifica si los permisos están actualizados comparando con /api/auth/me
   * Si el token está desactualizado, sugiere cerrar sesión
   */
  checkPermissionsStatus(): Observable<void> {
    console.log('[AuthService] Verificando estado de permisos...');
    const url = `${environment.apiBaseUrl}/api/auth/me`;
    return this.http.get<any>(url).pipe(
      tap((user) => {
        const permissions = user.permissions || user.Permissions || [];
        console.log('[AuthService] Permisos desde /api/auth/me:', permissions);

        const required = ['Customers.GetPaged', 'Customers.GetAll', 'Spaces.GetAll'];
        const missing = required.filter(perm => !permissions.includes(perm));

        if (missing.length > 0) {
          console.warn('[AuthService] ⚠️ FALTAN PERMISOS EN LA BASE DE DATOS:');
          missing.forEach(perm => console.warn('  ❌', perm));
          console.warn('[AuthService] Asigna estos permisos a tu rol en la base de datos.');
        } else {
          console.log('[AuthService] ✅ Todos los permisos requeridos están en /api/auth/me');
          console.warn('[AuthService] ⚠️ Si aún recibes 403, el token JWT está desactualizado.');
          console.warn('[AuthService] SOLUCIÓN: Cierra sesión e inicia sesión de nuevo para obtener un nuevo token.');
        }
      }),
      map(() => void 0),
      catchError((err) => {
        console.error('[AuthService] Error verificando permisos:', err);
        return of(void 0);
      })
    );
  }

  /**
   * Método de depuración: decodifica el token JWT y muestra los permisos
   * Ejecutar en la consola: authenticationService.debugToken()
   */
  debugToken(): void {
    const token = this.getAccessToken();
    if (!token) {
      console.warn('[AuthService] No hay token disponible');
      return;
    }

    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error('[AuthService] Token inválido: debe tener 3 partes');
        return;
      }

      const base64Url = parts[1];
      let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

      while (base64.length % 4) {
        base64 += '=';
      }

      const decoded = JSON.parse(atob(base64));

      console.log('=== TOKEN JWT DEBUG ===');
      console.log('Payload completo:', decoded);

      // Extraer permisos
      const permissions: string[] = [];

      // Buscar claim "Permission"
      if (decoded['Permission']) {
        const value = decoded['Permission'];
        if (Array.isArray(value)) {
          permissions.push(...value.filter((v: any) => typeof v === 'string'));
        } else if (typeof value === 'string') {
          permissions.push(value);
        }
      }

      // Buscar en todas las claves
      Object.keys(decoded).forEach(key => {
        const value = decoded[key];
        if (key.toLowerCase().includes('permission') && key !== 'Permission') {
          if (Array.isArray(value)) {
            permissions.push(...value.filter((v: any) => typeof v === 'string'));
          } else if (typeof value === 'string') {
            permissions.push(value);
          }
        }
      });

      // Buscar arrays que contengan permisos
      Object.keys(decoded).forEach(key => {
        const value = decoded[key];
        if (Array.isArray(value) && value.length > 0) {
          const possiblePerms = value.filter((v: any) =>
            typeof v === 'string' &&
            (v.includes('.') || v.match(/^[A-Z][a-zA-Z]*\.[A-Z][a-zA-Z]*$/))
          );
          if (possiblePerms.length > 0) {
            permissions.push(...possiblePerms);
          }
        }
      });

      const uniquePermissions = Array.from(new Set(permissions));

      console.log('=== PERMISOS ENCONTRADOS ===');
      console.log('Total:', uniquePermissions.length);
      uniquePermissions.forEach(perm => {
        console.log('  ✅', perm);
      });

      // Verificar permisos requeridos
      const required = ['Customers.GetPaged', 'Customers.GetAll', 'Spaces.GetAll'];
      console.log('=== VERIFICACIÓN DE PERMISOS REQUERIDOS ===');
      const missingPermissions: string[] = [];
      required.forEach(perm => {
        const has = uniquePermissions.includes(perm);
        if (has) {
          console.log('  ✅', perm, '- PRESENTE');
        } else {
          console.warn('  ❌', perm, '- FALTANTE');
          missingPermissions.push(perm);
        }
      });

      // Verificar permisos desde /api/auth/me
      const currentUser = this.getCurrentUserSnapshot();
      if (currentUser) {
        console.log('=== PERMISOS DESDE /api/auth/me ===');
        const mePermissions = currentUser.permissions || currentUser.Permissions || [];
        if (Array.isArray(mePermissions) && mePermissions.length > 0) {
          console.log('Total:', mePermissions.length);
          mePermissions.forEach((perm: string) => {
            console.log('  ✅', perm);
          });

          // Comparar con los del token
          const missingInToken = required.filter(perm =>
            !uniquePermissions.includes(perm) && mePermissions.includes(perm)
          );
          if (missingInToken.length > 0) {
            console.warn('=== ⚠️ PROBLEMA DETECTADO ===');
            console.warn('Los siguientes permisos están en /api/auth/me pero NO en el token JWT:');
            missingInToken.forEach(perm => console.warn('  ❌', perm));
            console.warn('');
            console.warn('SOLUCIÓN: El token JWT está desactualizado.');
            console.warn('Ejecuta: authenticationService.forceTokenRefresh()');
            console.warn('O cierra sesión e inicia sesión de nuevo.');
          }
        } else {
          console.warn('⚠️ No se encontraron permisos en /api/auth/me');
        }
      }

      if (missingPermissions.length > 0) {
        console.warn('');
        console.warn('=== RECOMENDACIÓN ===');
        console.warn('Faltan permisos en el token. Posibles soluciones:');
        console.warn('1. Ejecuta: authenticationService.forceTokenRefresh()');
        console.warn('2. O cierra sesión e inicia sesión de nuevo');
        console.warn('3. Verifica que los permisos estén asignados a tu rol en la base de datos');
      }

      console.log('=== FIN DEBUG ===');

      // Hacer disponible globalmente para depuración
      (window as any).__debugTokenPermissions = uniquePermissions;
      console.log('💡 Permisos también disponibles en: window.__debugTokenPermissions');

    } catch (error) {
      console.error('[AuthService] Error decodificando token:', error);
    }
  }
}
